import { SortTh } from '../../components/SortTh';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatCents, OFFER_STATUS_LABELS, OFFER_STATUSES, offerStatusLabel, type OfferSentVia, type OfferStatus, type OfferSummary, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import './offers.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (d: string) => dateFormat.format(new Date(`${d}T12:00:00`));

/** CSS-Klasse für den Status, „Manuell versendet“ hat eine eigene Farbe (KON-114) */
export const offerTone = (s: OfferStatus, sentVia: OfferSentVia = null) => `offer-status offer-status--${s === 'sent' && sentVia === 'manual' ? 'manual' : s}`;

/** Liste der Angebote mit Suche und Filter nach Status (KON-098) */
export function OffersPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? 1);
  const sort = params.get('sort') ?? '-createdAt';

  const list = useQuery({
    queryKey: ['offers', { q, status, page, sort }],
    queryFn: () => api<Paginated<OfferSummary>>('GET', `/offers${queryString({ q, status, page, sort, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const onSort = (next: string) => update({ sort: next === '-createdAt' ? '' : next });
  const fields: FilterField[] = [{ key: 'status', label: 'Status', type: 'select', options: [{ value: '', label: 'Alle' }, ...OFFER_STATUSES.map((s) => ({ value: s, label: OFFER_STATUS_LABELS[s] }))] }];

  return (
    <>
      <div className="page-head">
        <h1>Angebote</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        {can('offers.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/angebote/neu">Neues Angebot</Link>}
      </div>
      <FilterBar
        search={{ value: q, placeholder: 'Nummer, Betreff oder Kunde', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ status }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Angebot" sortKey="number" sort={sort} onSort={onSort} />
              <SortTh label="Kunde" sortKey="contact" sort={sort} onSort={onSort} />
              <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
              <SortTh label="Gültig bis" sortKey="validUntil" sort={sort} onSort={onSort} />
              <SortTh label="Brutto" sortKey="gross" sort={sort} onSort={onSort} first="desc" className="num" />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={5} className="empty">{q || status ? 'Keine Angebote zu dieser Suche.' : 'Noch keine Angebote. Legen Sie das erste über „Neues Angebot“ an.'}</td></tr>}
            {list.data?.data.map((o) => (
              <ExpandRow
                key={o.id}
                summary={
                  <Summary
                    title={<Link to={`/angebote/${o.id}`} className="strong-link">{o.number ?? 'Entwurf'}</Link>}
                    aside={<span className={offerTone(o.status, o.sentVia)}>{offerStatusLabel(o.status, o.sentVia)}</span>}
                    line2={`${o.contactName} · ${o.subject}`}
                    line3={`${formatCents(o.grossCents)}${o.validUntil ? ` · gültig bis ${day(o.validUntil)}` : ''}`}
                  />
                }
              >
                <td className="cell-desktop-title">
                  <Link to={`/angebote/${o.id}`} className="strong-link">{o.number ?? 'Entwurf'}</Link>
                  <div className="muted small">{o.subject}</div>
                </td>
                <td data-label="Kunde">{o.contactName}</td>
                <td data-label="Status"><span className={offerTone(o.status, o.sentVia)}>{offerStatusLabel(o.status, o.sentVia)}</span></td>
                <td data-label="Gültig bis">{o.validUntil ? day(o.validUntil) : '–'}</td>
                <td data-label="Brutto" className="num">{formatCents(o.grossCents)}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
